import { useEffect, useRef, useState } from "react";
import "../styles/achievements.css";

import heroBg  from "../photos/bg01.jpg";
import photo00 from "../photos/achievements00.jpg";
import photo01 from "../photos/achievements01.jpg";
import photo02 from "../photos/achievements02.jpg";
import photo03 from "../photos/achievements03.jpg";
import photo04 from "../photos/achievements04.jpg";
import photo05 from "../photos/achievements05.jpg";

/* ─── Data ─── */
const ACHIEVEMENTS = [
  {
    id: "ach-01",
    category: "Academic Excellence",
    era: "Basic Education",
    title: "With Honors",
    subtitle: "Grade 8, 9, 10 · Grade 11 (1st Sem) · Grade 12 (1st Sem)",
    icon: "★",
    color: "gold",
    stat: "5",
    statLabel: "Semesters",
    photo: photo00,
    detail: "Consistent academic honors throughout junior and senior high school — a streak built on discipline and genuine curiosity.",
  },
  {
    id: "ach-02",
    category: "Academic Excellence",
    era: "Basic Education",
    title: "With High Honors",
    subtitle: "Grade 11 (2nd Sem) · Grade 12 (2nd Sem) · SHS Overall Average",
    icon: "◆",
    color: "cyan",
    stat: "Top",
    statLabel: "SHS Tier",
    photo: photo01,
    detail: "Pushed further — reaching the highest academic tier in the final semesters of senior high. The SHS average sealed the recognition.",
  },
  {
    id: "ach-03",
    category: "College Honors",
    era: "FAITH Colleges",
    title: "Dean's Lister",
    subtitle: "2nd Year (1st Sem) · 3rd Year (1st Sem)",
    icon: "▲",
    color: "teal",
    stat: "2×",
    statLabel: "Dean's List",
    photo: photo02,
    detail: "Named to the Dean's List in back-to-back first semesters at FAITH Colleges, proving consistency at the tertiary level.",
  },
  {
    id: "ach-04",
    category: "College Honors",
    era: "FAITH Colleges",
    title: "President's Lister",
    subtitle: "1st Year · 2nd Year (2nd Sem) · 3rd Year (2nd Sem)",
    icon: "●",
    color: "gold",
    stat: "3×",
    statLabel: "Pres. List",
    photo: photo03,
    detail: "Three-time President's Lister — the highest college academic recognition — across consecutive second semesters and freshman year.",
  },
  {
    id: "ach-05",
    category: "Competition",
    era: "Engineering Cups",
    title: "Quiz Bee — 1st Runner Up",
    subtitle: "Engineering Cup · 1st Year & 3rd Year",
    icon: "⬡",
    color: "coral",
    stat: "2nd",
    statLabel: "Place × 2",
    photo: photo04,
    detail: "Placed 1st Runner Up in the Engineering Cup Quiz Bee — twice. Competing among the sharpest engineering minds in the college.",
  },
  {
    id: "ach-06",
    category: "Competition",
    era: "MTAP Awards",
    title: "MTAP Mathematics Awards",
    subtitle: "Champion (G7) · 1st Runner Up (G8) · Most Promising (G7 & G8)",
    icon: "π",
    color: "purple",
    stat: "4",
    statLabel: "MTAP Awards",
    photo: photo05,
    detail: "Mathematics was an early love. Champion in Grade 7 MTAP, Runner-Up in Grade 8, and dual 'Most Promising' recognition — a foundation that led here.",
  },
];

/* ═══════════════════════════════════════
   BALLOONS
   runKey changes on every mount → effect
   fires fresh on every page visit.
═══════════════════════════════════════ */
function Balloons({ runKey }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const colors = [
      "#44f9ff", "#FAF4E4", "#e86c6c", "#f0a84e",
      "#9b72f0", "#72f0a4", "#f072c4", "#f0e472",
    ];
    const shapes = ["●", "◆", "▲", "★"];

    const created = Array.from({ length: 26 }, (_, i) => ({
      id: i,
      left: `${4 + Math.random() * 92}%`,
      delay: `${Math.random() * 2.4}s`,
      duration: `${3.8 + Math.random() * 3}s`,
      size: 20 + Math.random() * 26,
      color: colors[Math.floor(Math.random() * colors.length)],
      shape: shapes[Math.floor(Math.random() * shapes.length)],
      wobble: `${(Math.random() > 0.5 ? 1 : -1) * (14 + Math.random() * 22)}px`,
    }));
    setItems(created);

    const timer = setTimeout(() => setItems([]), 8500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runKey]);

  return (
    <div className="balloon-layer" aria-hidden="true">
      {items.map((b) => (
        <div
          key={b.id}
          className="balloon"
          style={{
            left: b.left,
            animationDelay: b.delay,
            animationDuration: b.duration,
            fontSize: b.size,
            color: b.color,
            "--wobble": b.wobble,
          }}
        >
          {b.shape}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════
   STAR CATCHER MINI-GAME
═══════════════════════════════════════ */
function StarCatcher() {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    stars: [], paddle: { x: 110, w: 70, h: 10 },
    score: 0, misses: 0, running: false, raf: null, mx: 110,
  });
  const [display, setDisplay] = useState({ score: 0, misses: 0, started: false, over: false });

  const W = 220, H = 500, PADDLE_Y = H - 28, MAX_MISSES = 5;

  function spawnStar(s) {
    const types = [
      { char: "★", pts: 1, color: "#FAF4E4", speed: 1.2 },
      { char: "◆", pts: 2, color: "#44f9ff", speed: 1.6 },
      { char: "●", pts: 3, color: "#f0a84e", speed: 2.1 },
      { char: "▲", pts: 5, color: "#e86c6c", speed: 2.8 },
    ];
    const t = types[Math.floor(Math.random() * types.length)];
    s.stars.push({ x: 12 + Math.random() * (W - 24), y: -14, ...t, size: 13, id: Math.random() });
  }

  function loop(ctx, s, lastSpawn) {
    ctx.clearRect(0, 0, W, H);
    s.paddle.x += (s.mx - s.paddle.x) * 0.18;
    s.paddle.x = Math.max(0, Math.min(W - s.paddle.w, s.paddle.x));

    const now = performance.now();
    if (now - lastSpawn.t > 900 - Math.min(s.score * 8, 600)) {
      spawnStar(s); lastSpawn.t = now;
    }

    const alive = []; let newMisses = 0;
    for (const star of s.stars) {
      star.y += star.speed;
      if (star.y > PADDLE_Y - 4 && star.y < PADDLE_Y + 14 &&
          star.x > s.paddle.x - 4 && star.x < s.paddle.x + s.paddle.w + 4) {
        s.score += star.pts; continue;
      }
      if (star.y > H + 10) { newMisses++; continue; }
      alive.push(star);
    }
    s.stars = alive;
    if (newMisses > 0) s.misses += newMisses;

    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    for (const star of s.stars) {
      ctx.font = `${star.size}px sans-serif`;
      ctx.fillStyle = star.color;
      ctx.fillText(star.char, star.x, star.y);
    }

    ctx.beginPath();
    ctx.roundRect(s.paddle.x, PADDLE_Y, s.paddle.w, s.paddle.h, 5);
    ctx.fillStyle = "#44f9ff"; ctx.fill();

    for (let i = 0; i < MAX_MISSES; i++) {
      ctx.font = "11px sans-serif";
      ctx.fillStyle = i < s.misses ? "#e86c6c" : "#2a2a2a";
      ctx.fillText("♥", 12 + i * 18, 14);
    }

    setDisplay({ score: s.score, misses: s.misses, started: true, over: false });

    if (s.misses >= MAX_MISSES) {
      s.running = false;
      setDisplay({ score: s.score, misses: s.misses, started: true, over: true });
      return;
    }
    s.raf = requestAnimationFrame(() => loop(ctx, s, lastSpawn));
  }

  function start() {
    const s = stateRef.current;
    if (s.raf) cancelAnimationFrame(s.raf);
    s.stars = []; s.score = 0; s.misses = 0; s.running = true;
    s.paddle.x = W / 2 - 35; s.mx = W / 2 - 35;
    const ctx = canvasRef.current.getContext("2d");
    const lastSpawn = { t: performance.now() };
    setDisplay({ score: 0, misses: 0, started: true, over: false });
    s.raf = requestAnimationFrame(() => loop(ctx, s, lastSpawn));
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    const s = stateRef.current;
    function onMove(e) {
      const rect = canvas.getBoundingClientRect();
      const cx = e.clientX ?? (e.touches?.[0]?.clientX ?? 0);
      s.mx = (cx - rect.left) * (W / rect.width) - s.paddle.w / 2;
    }
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("touchmove", (e) => { e.preventDefault(); onMove(e); }, { passive: false });
    return () => {
      canvas.removeEventListener("mousemove", onMove);
      if (s.raf) cancelAnimationFrame(s.raf);
    };
  }, []);

  return (
    <div className="game-wrap">
      <p className="game-title">— Catch the Stars —</p>
      <p className="game-sub">Move your cursor over the canvas to steer the paddle</p>
      <div className="game-score-row">
        <span className="game-score-val">{display.score}</span>
        <span className="game-score-label">pts</span>
      </div>
      <div className="game-canvas-wrap">
        <canvas ref={canvasRef} width={W} height={H} className="game-canvas" />
        {!display.started && (
          <div className="game-overlay">
            <p className="game-overlay-title">Star Catcher</p>
            <p className="game-overlay-body">
              Catch falling symbols with the glowing paddle.<br />Rarer symbols = more points!
            </p>
            <div className="game-legend">
              <span style={{ color: "#FAF4E4" }}>★ 1pt</span>
              <span style={{ color: "#44f9ff" }}>◆ 2pts</span>
              <span style={{ color: "#f0a84e" }}>● 3pts</span>
              <span style={{ color: "#e86c6c" }}>▲ 5pts</span>
            </div>
            <button className="game-btn" onClick={start}>Start Game</button>
          </div>
        )}
        {display.over && (
          <div className="game-overlay">
            <p className="game-overlay-title">Game Over</p>
            <p className="game-overlay-score">{display.score}</p>
            <p className="game-overlay-body">points collected</p>
            <button className="game-btn" onClick={start}>Play Again</button>
          </div>
        )}
      </div>
      <div className="game-hint-row">
        <span>★ White · 1pt</span>
        <span>◆ Cyan · 2pt</span>
        <span>● Amber · 3pt</span>
        <span>▲ Red · 5pt</span>
      </div>
    </div>
  );
}

/* ─── Intersection Reveal ─── */
function useReveal() {
  useEffect(() => {
    const nodes = document.querySelectorAll(".ach-reveal");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("is-visible"); obs.unobserve(e.target); }
      }),
      { threshold: 0.08, rootMargin: "0px 0px -20px 0px" }
    );
    nodes.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, []);
}

/* ─── Achievement Card ─── */
function AchCard({ ach, index }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      className={`ach-card ach-reveal ach-card--${ach.color}`}
      style={{ transitionDelay: `${index * 0.08}s` }}
      onClick={() => setFlipped((f) => !f)}
      title="Click to flip"
    >
      <div className={`ach-card__inner ${flipped ? "is-flipped" : ""}`}>

        {/* ── FRONT ── */}
        <div className="ach-card__face ach-card__front">
          {/* Stat badge top-right */}
          <div className="ach-card__stat-wrap">
            <span className="ach-card__stat">{ach.stat}</span>
            <span className="ach-card__stat-label">{ach.statLabel}</span>
          </div>

          {/* Center photo */}
          <div className="ach-card__photo-wrap">
            <img src={ach.photo} alt={ach.title} className="ach-card__photo" />
            <div className="ach-card__photo-overlay" />
          </div>

          {/* Bottom text */}
          <div className="ach-card__body">
            <p className="ach-card__era">{ach.era}</p>
            <p className="ach-card__title">{ach.title}</p>
            <p className="ach-card__subtitle">{ach.subtitle}</p>
          </div>

          <span className="ach-card__flip-hint">flip ↻</span>
        </div>

        {/* ── BACK ── */}
        <div className="ach-card__face ach-card__back">
          <p className="ach-card__back-category">{ach.category}</p>
          <p className="ach-card__back-detail">{ach.detail}</p>
          <span className="ach-card__flip-hint">flip ↻</span>
        </div>

      </div>
    </div>
  );
}

/* ─── Timeline Dot Row ─── */
function TimelineDots() {
  const eras = [
    { label: "Grade School", sub: "MTAP Champ",  color: "purple" },
    { label: "Junior High",  sub: "With Honors", color: "teal" },
    { label: "Senior High",  sub: "High Honors", color: "gold" },
    { label: "College",      sub: "Pres. Lister",color: "cyan" },
  ];
  return (
    <div className="ach-timeline ach-reveal">
      <div className="ach-timeline__track">
        {eras.map((e, i) => (
          <div key={i} className="ach-timeline__node">
            <div className={`ach-timeline__dot ach-timeline__dot--${e.color}`} />
            <p className="ach-timeline__label">{e.label}</p>
            <p className="ach-timeline__sub">{e.sub}</p>
            {i < eras.length - 1 && <div className="ach-timeline__line" />}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   MAIN EXPORT
═══════════════════════════════════════ */
export default function Achievements() {
  /*
    useState initializer runs once per mount.
    Date.now() gives a unique key every time
    the component mounts (i.e. every page visit),
    so the Balloons effect always fires fresh.
  */
  const [balloonKey] = useState(() => Date.now());
  useReveal();

  return (
    <div className="ach-page">
      <Balloons runKey={balloonKey} />

      {/* ═══ HERO with background photo ═══ */}
      <div className="ach-hero">
        <img src={heroBg} alt="" className="ach-hero__bg" aria-hidden="true" />
        <div className="ach-hero__overlay" />
        <div className="ach-hero__content">
          <h1 className="ach-hero__title">A Record of Excellence</h1>
          <p className="ach-hero__sub">
            From Grade 7 mathematics podiums to college President's Lists —<br />
            each recognition is a chapter in a longer story.
          </p>
        </div>
      </div>

      {/* ═══ TIMELINE STRIP ═══ */}
      <TimelineDots />

      <div className="ach-divider ach-reveal" />

      {/* ═══ TWO-COLUMN LAYOUT ═══ */}
      <div className="ach-columns">

        {/* LEFT — 70% */}
        <div className="ach-left">
          <p className="ach-section-label ach-reveal">— Click any card to reveal the story —</p>
          <div className="ach-grid">
            {ACHIEVEMENTS.map((a, i) => (
              <AchCard key={a.id} ach={a} index={i} />
            ))}
          </div>

          {/* Stats strip */}
          <div className="ach-stats ach-reveal">
            <div className="ach-stat">
              <span className="ach-stat__val">5×</span>
              <span className="ach-stat__lbl">Pres. / Dean's</span>
            </div>
            <div className="ach-stat">
              <span className="ach-stat__val">4</span>
              <span className="ach-stat__lbl">MTAP Awards</span>
            </div>
            <div className="ach-stat">
              <span className="ach-stat__val">2</span>
              <span className="ach-stat__lbl">Quiz Bee Podiums</span>
            </div>
            <div className="ach-stat">
              <span className="ach-stat__val">8+</span>
              <span className="ach-stat__lbl">Total Honors</span>
            </div>
          </div>
        </div>

        {/* RIGHT — 30% */}
        <div className="ach-right ach-reveal">
          <StarCatcher />
        </div>

      </div>
    </div>
  );
}